import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Search, Plus, FileText, Download, Library } from 'lucide-react';
import { STATUSES, CATEGORY_FILTERS, PROJECT_TYPES, StatusPill, itemState, money } from './utils';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export default function ProposalsList({ proposals, clientMap, loading, onOpenDetail, onNewProposal, onOpenTemplates, onExportReport, exporting }) {
  const navigate = useNavigate();
  const { effectiveRole } = useAuth();
  const isAdmin = effectiveRole === 'Administrator' || effectiveRole === 'Owner';
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [customTypes, setCustomTypes] = useState([]);

  const fetchTypes = () => api.get('/options/project_type').then(r => setCustomTypes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { fetchTypes(); }, []);
  // Also refresh whenever the proposals list changes (e.g. after creating a proposal
  // with a brand-new type) so the Type filter stays in sync without a page reload.
  useEffect(() => { fetchTypes(); }, [proposals]);

  const list = Array.isArray(proposals) ? proposals : [];

  // Type filter (option B): keep the built-in category groups AND surface any individual
  // project types that no category covers (custom-added ones + "Other"), so newly added
  // types are always filterable.
  const categorizedTypes = new Set(Object.values(CATEGORY_FILTERS).flat());
  const seenT = new Set();
  const extraTypes = [];
  [...PROJECT_TYPES, ...customTypes, ...list.map(p => p.project_type)].forEach(t => {
    const v = (t || '').trim();
    if (v && !categorizedTypes.has(v) && !seenT.has(v.toLowerCase())) { seenT.add(v.toLowerCase()); extraTypes.push(v); }
  });
  extraTypes.sort((a, b) => a.localeCompare(b));

  const filtered = list.filter(p => {
    if (statusFilter !== 'All' && p.status !== statusFilter) return false;
    if (categoryFilter !== 'all') {
      if (categoryFilter.startsWith('cat:')) {
        const allowed = CATEGORY_FILTERS[categoryFilter.slice(4)] || [];
        if (!allowed.includes(p.project_type)) return false;
      } else if (categoryFilter.startsWith('type:')) {
        if (p.project_type !== categoryFilter.slice(5)) return false;
      }
    }
    const q = search.toLowerCase();
    if (q && !((p.title || '').toLowerCase().includes(q) || (p.proposal_number || '').toLowerCase().includes(q) || (clientMap[p.client_id] || '').toLowerCase().includes(q))) return false;
    return true;
  });

  return (
    <div className="space-y-4 animate-fade-in" data-testid="proposals-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-semibold text-lg tracking-tight text-foreground">Proposals</h1>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" className="border-border" onClick={() => navigate('/settings/task-library')} data-testid="manage-services-library-btn"><Library className="h-4 w-4 mr-1" /> Manage Services Library</Button>
          )}
          <Button variant="outline" className="border-border" onClick={onOpenTemplates} data-testid="description-templates-btn"><FileText className="h-4 w-4 mr-1" /> Description Templates</Button>
          <Button className="bg-primary text-primary-foreground" onClick={onNewProposal} data-testid="new-proposal-btn"><Plus className="h-4 w-4 mr-1" /> New Proposal</Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search proposals..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="proposals-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 bg-card border-border text-sm" data-testid="proposals-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="All" className="text-sm">All Statuses</SelectItem>
            {STATUSES.map(s => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter} onOpenChange={(o) => { if (o) fetchTypes(); }}>
          <SelectTrigger className="w-44 bg-card border-border text-sm" data-testid="proposals-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all" className="text-sm">All Types</SelectItem>
            <SelectGroup>
              <SelectLabel className="text-xs text-muted-foreground">Categories</SelectLabel>
              {Object.keys(CATEGORY_FILTERS).map(c => <SelectItem key={c} value={`cat:${c}`} className="text-sm">{c}</SelectItem>)}
            </SelectGroup>
            {extraTypes.length > 0 && (
              <SelectGroup>
                <SelectLabel className="text-xs text-muted-foreground">Project Types</SelectLabel>
                {extraTypes.map(t => <SelectItem key={t} value={`type:${t}`} className="text-sm" data-testid={`proposals-type-${t}`}>{t}</SelectItem>)}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
        <Button variant="outline" className="border-border" onClick={onExportReport} disabled={exporting} data-testid="export-proposal-report-btn">
          <Download className="h-4 w-4 mr-1" /> {exporting ? 'Exporting...' : 'Export Report'}
        </Button>
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs text-muted-foreground">Proposal #</TableHead>
              <TableHead className="text-xs text-muted-foreground">Title</TableHead>
              <TableHead className="text-xs text-muted-foreground">Client</TableHead>
              <TableHead className="text-xs text-muted-foreground">Type</TableHead>
              <TableHead className="text-xs text-muted-foreground">Status</TableHead>
              <TableHead className="text-xs text-muted-foreground text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">Loading proposals...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">No proposals found. Click "New Proposal" to create one.</TableCell></TableRow>
            ) : filtered.map(p => {
              const inc = (p.line_items || []).filter(it => itemState(it) === 'included');
              const sub = inc.reduce((s, it) => s + (Number(it.fee) || 0), 0) + (Number(p.contingency_amount) || 0);
              return (
                <TableRow key={p.id} className="border-border hover:bg-secondary/30 cursor-pointer" onClick={() => onOpenDetail(p)} data-testid={`proposal-row-${p.id}`}>
                  <TableCell className="text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>{p.proposal_number || 'Draft'}</TableCell>
                  <TableCell className="text-sm font-medium" style={{ color: '#010A21' }}>{p.title || 'Untitled'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{clientMap[p.client_id] || '\u00A0'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{p.project_type || '\u00A0'}</TableCell>
                  <TableCell><StatusPill status={p.status} /></TableCell>
                  <TableCell className="text-right text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>{money(sub)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
